import React, { useEffect, useState } from "react";
import { pldtConfig } from "../../config.js";
import IconButton from "../buttons/IconButton";
import FacebookIcon from "../icons/FacebookIcon.js";
import { checkURL, transformMenu } from "../../helpers/helpers.js";
import TwitterXIcon from "../icons/TwitterXIcon.js";
import InstagramIcon from "../icons/InstagramIcon.js";
import YoutubeIcon from "../icons/YoutubeIcon.js";
import FooterRedirectBtn from "../buttons/FooterRedirectBtn.js";

const Footer = () => {
  const [aboutMenu, setAboutMenu] = useState([]);
  const [govMenu, setGovMenu] = useState([]);
  const [irMenu, setIrMenu] = useState([]);
  const [highlightMenu, setHighlightMenu] = useState([]);
  const [socials, setSocials] = useState([]);
  const [btmRightMenu, setBtmRightMenu] = useState([]);
  const [talkToUs, setTalkToUs] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    // Define the API endpoint for fetching the global footer data
    const fetchURL = pldtConfig.apiUrl + "api/v1/global-footer";

    // Fetch the global footer data
    fetch(fetchURL)
      .then((response) => response.json()) // Parse the JSON response
      .then((response) => {
        // Transform the different menu sections using the transformMenu function
        const about_menu = transformMenu(response[0].about_menu);
        const gov_menu = transformMenu(response[0].gov_menu);
        const ir_menu = transformMenu(response[0].ir_menu);
        const highlight_menu = transformMenu(response[0].highlight_menu);
        const social_menu = transformMenu(response[0].social_medias);
        const talk_to_us = transformTalkToUs(response[0].talk_to_us);
        const bottom_right_menu = transformMenu(response[0].bottom_right_menu);

        // Set the state with the fetched and transformed data
        setData(response[0]);
        setTalkToUs(talk_to_us);
        setSocials(social_menu);
        setAboutMenu(about_menu);
        setGovMenu(gov_menu);
        setIrMenu(ir_menu);
        setHighlightMenu(highlight_menu);
        setBtmRightMenu(bottom_right_menu);
      })
      .catch((err) => {
        // Log any errors encountered during the fetch operation
        console.error("Error fetching global footer:", err);
      });
  }, []); // Empty dependency array ensures this runs only once on component mount

  // Function to transform the "Talk To Us" HTML content into structured data
  const transformTalkToUs = (htmlString) => {
    const parser = new DOMParser(); // Create a DOM parser to parse the HTML string
    const doc = parser.parseFromString(htmlString, "text/html"); // Parse the HTML string

    // Extract the title, body, and CTA (call-to-action) link
    const title = doc
      .querySelector(".field--name-field-title .field__item")
      .textContent.trim();
    const body = doc
      .querySelector(".field--name-field-body .field__item")
      .textContent.trim();
    const ctaLink = doc.querySelector(".field--name-field-cta .field__item a");

    // Return the structured "Talk To Us" data
    return {
      title: title,
      body: body,
      cta: {
        title: ctaLink.textContent.trim(),
        url: ctaLink.href,
      },
    };
  };

  // Function to wrap phone numbers with <a> tags for dialing
  const wrapPhoneNumbersWithATag = (str) => {
    return str.replace(/(\+?[\d\s\(\)\-]+[\d])/g, '<a href="tel:$1">$1</a>'); // Match phone numbers and wrap with <a> tag
  };

  // Function to create a Google Maps link from an address string
  const createGoogleMapsLink = (address) => {
    const encodedAddress = encodeURIComponent(address); // Encode the address for URL
    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`; // Return the Google Maps search URL
  };

  return (
    <footer>
      <div className="c-footer">
        <div className="top-section">
          <div className="left-content">
            {/* Talk to us Mobile */}
            {talkToUs && (
              <div className="c-footer-talk-to-us-mobile">
                <div className="texts">
                  <h5>{talkToUs.title}</h5>
                  <p>{talkToUs.body}</p>
                </div>
                <IconButton
                  hideIcon={true}
                  title={talkToUs.cta.title}
                  url={talkToUs.cta.url}
                />
              </div>
            )}
            <div className="logo">
              {data && <img src={pldtConfig.appDomain + data.footer_logo} />}
            </div>
            <div className="list-socials">
              <ul className="list">
                {/* Address */}
                <li>
                  <div className="list-item">
                    <div className="icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M12 6.1875C11.2954 6.1875 10.6066 6.39644 10.0208 6.78789C9.43493 7.17934 8.97832 7.73573 8.70868 8.38669C8.43904 9.03765 8.36849 9.75395 8.50595 10.445C8.64341 11.1361 8.98271 11.7708 9.48093 12.2691C9.97916 12.7673 10.6139 13.1066 11.305 13.244C11.996 13.3815 12.7123 13.311 13.3633 13.0413C14.0143 12.7717 14.5707 12.3151 14.9621 11.7292C15.3536 11.1434 15.5625 10.4546 15.5625 9.75C15.5625 8.80517 15.1872 7.89903 14.5191 7.23093C13.851 6.56283 12.9448 6.1875 12 6.1875ZM12 12.1875C11.5179 12.1875 11.0466 12.0445 10.6458 11.7767C10.245 11.5089 9.93253 11.1282 9.74804 10.6828C9.56356 10.2374 9.51528 9.7473 9.60934 9.27447C9.70339 8.80164 9.93554 8.36732 10.2764 8.02643C10.6173 7.68554 11.0516 7.45339 11.5245 7.35934C11.9973 7.26528 12.4874 7.31356 12.9328 7.49804C13.3782 7.68253 13.7589 7.99495 14.0267 8.3958C14.2945 8.79664 14.4375 9.26791 14.4375 9.75C14.4375 10.3965 14.1807 11.0165 13.7236 11.4736C13.2665 11.9307 12.6465 12.1875 12 12.1875ZM12 1.6875C9.86245 1.68998 7.81317 2.54022 6.30169 4.05169C4.79022 5.56317 3.93998 7.61245 3.9375 9.75C3.9375 12.6478 5.28188 15.7256 7.82531 18.6506C8.97329 19.9773 10.2653 21.1722 11.6775 22.2131C11.772 22.2793 11.8846 22.3148 12 22.3148C12.1154 22.3148 12.228 22.2793 12.3225 22.2131C13.7347 21.1722 15.0267 19.9773 16.1747 18.6506C18.7181 15.7256 20.0625 12.6506 20.0625 9.75C20.06 7.61245 19.2098 5.56317 17.6983 4.05169C16.1868 2.54022 14.1375 1.68998 12 1.6875ZM12 21.0478C10.5938 19.9622 5.0625 15.3394 5.0625 9.75C5.0625 7.91006 5.79341 6.14548 7.09445 4.84445C8.39548 3.54341 10.1601 2.8125 12 2.8125C13.8399 2.8125 15.6045 3.54341 16.9056 4.84445C18.2066 6.14548 18.9375 7.91006 18.9375 9.75C18.9375 15.3394 13.4062 19.9622 12 21.0478Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                    {data && (
                      <a
                        className="details"
                        href={createGoogleMapsLink(data.address)}
                        dangerouslySetInnerHTML={{
                          __html: data.address,
                        }}
                      />
                    )}
                  </div>
                </li>
                {/* Contact */}
                <li>
                  <div className="list-item">
                    <div className="icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M20.7741 15.0282L16.3453 13.0435C16.1447 12.9575 15.9257 12.9229 15.7083 12.9429C15.4909 12.9629 15.282 13.0368 15.1003 13.1579C15.0819 13.1697 15.0643 13.1829 15.0478 13.1972L12.7331 15.166C12.7091 15.1791 12.6824 15.1864 12.655 15.1874C12.6277 15.1884 12.6005 15.183 12.5756 15.1716C11.0878 14.4535 9.54657 12.9216 8.82564 11.4544C8.81363 11.4299 8.80739 11.403 8.80739 11.3757C8.80739 11.3484 8.81363 11.3214 8.82564 11.2969L10.8009 8.95318C10.8151 8.93584 10.8283 8.91768 10.8403 8.8988C10.9598 8.71646 11.0319 8.50725 11.0502 8.29006C11.0685 8.07286 11.0325 7.85452 10.9453 7.65474L8.9747 3.23349C8.86279 2.9725 8.66925 2.75484 8.42313 2.61319C8.17701 2.47154 7.89159 2.41353 7.6097 2.44787C6.3846 2.60893 5.26006 3.21056 4.44622 4.14034C3.63238 5.07011 3.18494 6.26441 3.18751 7.50005C3.18751 14.8407 9.15939 20.8126 16.5 20.8126C17.7356 20.8149 18.9298 20.3674 19.8595 19.5536C20.7893 18.7398 21.3909 17.6154 21.5522 16.3904C21.5865 16.1098 21.5292 15.8257 21.389 15.5802C21.2488 15.3348 21.0332 15.1412 20.7741 15.0282ZM16.5 19.6876C9.78001 19.6876 4.31251 14.2201 4.31251 7.50005C4.30938 6.53811 4.65658 5.60793 5.28924 4.88331C5.9219 4.15869 6.79675 3.6892 7.75032 3.56255H7.77189C7.80967 3.56326 7.84635 3.57536 7.87713 3.59728C7.90791 3.61919 7.93136 3.6499 7.94439 3.68537L9.92251 8.10193C9.93379 8.12648 9.93963 8.15319 9.93963 8.18021C9.93963 8.20723 9.93379 8.23394 9.92251 8.25849L7.94345 10.6079C7.92868 10.6246 7.91521 10.6425 7.90314 10.6613C7.7793 10.8504 7.70637 11.0682 7.69143 11.2937C7.67648 11.5192 7.72002 11.7447 7.81782 11.9485C8.64939 13.651 10.365 15.3535 12.0863 16.1851C12.2912 16.2823 12.5178 16.3248 12.7441 16.3084C12.9703 16.2919 13.1885 16.2172 13.3772 16.0913C13.395 16.0791 13.4128 16.066 13.4297 16.0519L15.7434 14.0832C15.7663 14.0709 15.7915 14.0637 15.8174 14.0621C15.8433 14.0605 15.8692 14.0645 15.8934 14.0738L20.3231 16.0585C20.3593 16.0739 20.3897 16.1003 20.4101 16.1339C20.4305 16.1676 20.4397 16.2068 20.4366 16.246C20.3106 17.2 19.8415 18.0755 19.1171 18.7089C18.3926 19.3422 17.4623 19.6901 16.5 19.6876Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                    {data && (
                      <div
                        className="details"
                        dangerouslySetInnerHTML={{
                          __html: wrapPhoneNumbersWithATag(data.contact),
                        }}
                      />
                    )}
                  </div>
                </li>
                {/* Fax */}
                <li>
                  <div className="list-item">
                    <div className="icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M20.1253 6.9375H18.5625V3.75C18.5625 3.60082 18.5032 3.45774 18.3977 3.35225C18.2923 3.24676 18.1492 3.1875 18 3.1875H6C5.85082 3.1875 5.70774 3.24676 5.60225 3.35225C5.49676 3.45774 5.4375 3.60082 5.4375 3.75V6.9375H3.87469C2.66906 6.9375 1.6875 7.86281 1.6875 9V16.5C1.6875 16.6492 1.74676 16.7923 1.85225 16.8977C1.95774 17.0032 2.10082 17.0625 2.25 17.0625H5.4375V20.25C5.4375 20.3992 5.49676 20.5423 5.60225 20.6477C5.70774 20.7532 5.85082 20.8125 6 20.8125H18C18.1492 20.8125 18.2923 20.7532 18.3977 20.6477C18.5032 20.5423 18.5625 20.3992 18.5625 20.25V17.0625H21.75C21.8992 17.0625 22.0423 17.0032 22.1477 16.8977C22.2532 16.7923 22.3125 16.6492 22.3125 16.5V9C22.3125 7.86281 21.3309 6.9375 20.1253 6.9375ZM6.5625 4.3125H17.4375V6.9375H6.5625V4.3125ZM17.4375 19.6875H6.5625V14.8125H17.4375V19.6875ZM21.1875 15.9375H18.5625V14.25C18.5625 14.1008 18.5032 13.9577 18.3977 13.8523C18.2923 13.7468 18.1492 13.6875 18 13.6875H6C5.85082 13.6875 5.70774 13.7468 5.60225 13.8523C5.49676 13.9577 5.4375 14.1008 5.4375 14.25V15.9375H2.8125V9C2.8125 8.48344 3.28875 8.0625 3.87469 8.0625H20.1253C20.7113 8.0625 21.1875 8.48344 21.1875 9V15.9375ZM18.5625 10.875C18.5625 11.0604 18.5075 11.2417 18.4045 11.3958C18.3015 11.55 18.1551 11.6702 17.9838 11.7411C17.8125 11.8121 17.624 11.8307 17.4421 11.7945C17.2602 11.7583 17.0932 11.669 16.9621 11.5379C16.831 11.4068 16.7417 11.2398 16.7055 11.0579C16.6693 10.876 16.6879 10.6875 16.7589 10.5162C16.8298 10.3449 16.95 10.1985 17.1042 10.0955C17.2583 9.99248 17.4396 9.9375 17.625 9.9375C17.8736 9.9375 18.1121 10.0363 18.2879 10.2121C18.4637 10.3879 18.5625 10.6264 18.5625 10.875Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                    {data && (
                      <a href={`tel:${data.fax}`} className="details">
                        {data.fax}
                      </a>
                    )}
                  </div>
                </li>
                {/* Email */}
                <li>
                  <div className="list-item">
                    <div className="icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M21 4.6875H3C2.85082 4.6875 2.70774 4.74676 2.60225 4.85225C2.49676 4.95774 2.4375 5.10082 2.4375 5.25V18C2.4375 18.3481 2.57578 18.6819 2.82192 18.9281C3.06806 19.1742 3.4019 19.3125 3.75 19.3125H20.25C20.5981 19.3125 20.9319 19.1742 21.1781 18.9281C21.4242 18.6819 21.5625 18.3481 21.5625 18V5.25C21.5625 5.10082 21.5032 4.95774 21.3977 4.85225C21.2923 4.74676 21.1492 4.6875 21 4.6875ZM19.5544 5.8125L12 12.7369L4.44562 5.8125H19.5544ZM20.25 18.1875H3.75C3.70027 18.1875 3.65258 18.1677 3.61742 18.1326C3.58225 18.0974 3.5625 18.0497 3.5625 18V6.52875L11.625 13.9144C11.7287 14.0092 11.8641 14.0619 12.0047 14.0619C12.1452 14.0619 12.2807 14.0092 12.3844 13.9144L20.4375 6.52875V18C20.4375 18.0497 20.4177 18.0974 20.3826 18.1326C20.3474 18.1677 20.2997 18.1875 20.25 18.1875Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                    {data && (
                      <a href={`mailto:${data.email}`} className="details">
                        {data.email}
                      </a>
                    )}
                  </div>
                </li>
              </ul>
              <ul className="socials">
                {socials.map((item, index) => (
                  <li key={index}>
                    <a href={item.url} target="_blank">
                      {index === 0 && <FacebookIcon />}
                      {index === 1 && <InstagramIcon />}
                      {index === 2 && <TwitterXIcon />}
                      {index === 3 && <YoutubeIcon />}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="right-content">
            {/* Talk to us Desktop */}
            {talkToUs && (
              <div className="c-footer-talk-to-us">
                <div className="texts">
                  <h5>{talkToUs.title}</h5>
                  <p>{talkToUs.body}</p>
                </div>
                <IconButton
                  hideIcon={true}
                  title={talkToUs.cta.title}
                  url={talkToUs.cta.url}
                />
              </div>
            )}
            <div className="c-footer-menus">
              <div className="menu">
                <FooterRedirectBtn
                  customClass="menu-title"
                  label={"About"}
                  url={"/about-us"}
                />
                <ul>
                  {aboutMenu.map((item, index) => (
                    <li key={index}>
                      <FooterRedirectBtn label={item.label} url={item.url} />
                    </li>
                  ))}
                </ul>
                <div className="mobile-separator"></div>
              </div>
              <div className="menu">
                <FooterRedirectBtn
                  customClass="menu-title"
                  label={"Governance"}
                  url={"/corporate-governance-in-pldt"}
                />
                <ul>
                  {govMenu.map((item, index) => (
                    <li key={index}>
                      <FooterRedirectBtn label={item.label} url={item.url} />
                    </li>
                  ))}
                </ul>
                <div className="mobile-separator"></div>
              </div>
              <div className="menu">
                <FooterRedirectBtn
                  customClass="menu-title"
                  label={"Investor Relations"}
                  url={"/investor-relations"}
                />
                <ul>
                  {irMenu.map((item, index) => (
                    <li key={index}>
                      <FooterRedirectBtn label={item.label} url={item.url} />
                    </li>
                  ))}
                </ul>
                <div className="mobile-separator"></div>
              </div>
              <ul className="highlight-links">
                {highlightMenu.map((item, index) => (
                  <li key={index}>
                    <FooterRedirectBtn label={item.label} url={item.url} />
                    <div className="mobile-separator"></div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="bottom-section">
          {data && <span>{data.copyright_title}</span>}
          <ul>
            {btmRightMenu.map((item, index) => (
              <li key={index}>
                <a href={checkURL(item.url)}>{item.label}</a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
